/**
 * The one header every signed-in page wears.
 *
 * It exists because the app stopped being a single screen: the dashboard, the
 * profile page and the admin page all need the same three things — a way back
 * to the other pages, who you are signed in as, and a way out. Duplicating that
 * per page is how a "Sign out" button ends up on two of the three.
 *
 * The `children` slot is what keeps the dashboard's header the dashboard's
 * header: the window picker sits inside this bar, between the nav and the
 * account block, exactly where it always did. Pages with no bar controls pass
 * nothing and the layout collapses around it.
 *
 * The worked example for the i18n rollout: every user-visible string here goes
 * through `t()` against a `nav.*` key, and the language switch itself sits in
 * this bar — beside the account block, so it is reachable from the dashboard,
 * the profile page and the admin page alike. Read `src/i18n/en.ts` for the key
 * convention before translating anything else.
 *
 * The Admin link is hidden from members for the same reason the route is
 * gated — but the hiding is cosmetic. `/api/v1/admin/*` is enforced server-side
 * by `require_role(Role.SUPERADMIN)`; a member who types the URL is redirected
 * because an unexplained 403 is a worse answer, not because the link is gone.
 */
import { NavLink } from 'react-router-dom'
import type { ReactNode } from 'react'

import { useAuth } from '../auth/useAuth'
import { LanguageSwitcher, useI18n } from '../i18n'

export const SUPERADMIN = 'superadmin'

const LINK_BASE =
  'rounded px-1.5 py-0.5 text-xs transition-colors hover:text-fg ' +
  'aria-[current=page]:text-fg aria-[current=page]:font-medium'

function navClass({ isActive }: { isActive: boolean }): string {
  return `${LINK_BASE} ${isActive ? 'text-fg' : 'text-muted'}`
}

export function AppHeader({ children }: { children?: ReactNode }) {
  const { user, logout } = useAuth()
  const { t } = useI18n()
  const isSuperadmin = user?.role === SUPERADMIN

  return (
    <header className="sticky top-0 z-20 border-b border-border bg-bg/95 backdrop-blur">
      <div className="mx-auto flex max-w-[110rem] flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
        <h1 className="text-sm font-semibold text-fg">{t('nav.header.title')}</h1>

        <nav aria-label={t('nav.header.mainNav')} className="flex items-center gap-1">
          {/* `end` so "/" is only current on the dashboard itself, rather than
              lighting up on every page below it. */}
          <NavLink to="/" end className={navClass}>
            {t('nav.header.dashboard')}
          </NavLink>
          <NavLink to="/profile" className={navClass}>
            {t('nav.header.profile')}
          </NavLink>
          {isSuperadmin && (
            <NavLink to="/admin" className={navClass}>
              {t('nav.header.admin')}
            </NavLink>
          )}
        </nav>

        {children}

        <div className="ml-auto flex items-center gap-3 text-xs text-muted">
          {user && <span className="num truncate">{user.email}</span>}
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => void logout()}
            className="rounded border border-border px-1.5 py-0.5 hover:text-fg"
          >
            {t('nav.header.signOut')}
          </button>
        </div>
      </div>
    </header>
  )
}

/**
 * Header + page body, for the routes that have no layout of their own.
 *
 * The dashboard is not one of them: it renders `<AppHeader/>` itself so it can
 * put the window picker in the bar, and its main column is wider than a form
 * page wants to be.
 *
 * This owns the ONE `<main>` on every route it wraps, which is why the width is
 * a prop here rather than a wrapper inside the page. A page that renders its own
 * `<main>` inside this one gives the document two nested `main` landmarks —
 * invalid HTML, and two "main" regions announced to assistive tech — and the
 * inner width request loses to the outer clamp anyway, which is exactly how the
 * six-column team table ended up scrolling sideways at every window size.
 */
export function AppShell({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="min-h-full bg-bg">
      <AppHeader />
      <main
        className={`mx-auto space-y-6 px-4 py-6 ${wide ? 'max-w-[70rem]' : 'max-w-3xl'}`}
      >
        {children}
      </main>
    </div>
  )
}
